import { useState, useEffect, useCallback, useRef } from "react";
import { io } from "socket.io-client";
import axios from "axios";
import { toast } from "sonner";

// Get API URL from environment
import { BACKEND_URL } from "@/config/api";
const API = `${BACKEND_URL}/api`;

// WebSocket URL (same as API but for Socket.IO)
const WS_URL = BACKEND_URL;

// Polling interval as fallback (only used if WebSocket fails)
const POLL_INTERVAL = 10000;

export const useRealtime = (enabled = true, token = null) => {
  const [events, setEvents] = useState([]);
  const [connected, setConnected] = useState(false);
  const [connectionType, setConnectionType] = useState(null); // 'websocket' | 'polling'
  const socketRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  const lastTimestampRef = useRef(null);

  const handleEvent = useCallback((event) => {
    setEvents((prev) => [...prev, event]);
    
    const fromName = event.from_name || 'Someone';
    
    switch (event.type) {
      case "item_completed":
        toast.success(`${fromName} completed "${event.data?.title || 'an item'}"! 🎉`, {
          duration: 5000,
        });
        break;
      case "item_added":
        toast.info(`${fromName} added "${event.data?.title || 'a new item'}" to the list`, {
          duration: 4000,
        });
        break;
      case "comment_added":
        toast.info(`${fromName} commented on "${event.data?.item_title || 'an item'}"`, {
          duration: 4000,
        });
        break;
      case "partner_linked":
        toast.success(`You're now connected with ${fromName}! 💕`, {
          duration: 6000,
        });
        break;
      case "group_joined":
        toast.info(`${fromName} joined the group`, {
          duration: 4000,
        });
        break;
      case "photo_added":
        toast.info(`${fromName} added a photo to "${event.data?.item_title || 'an item'}"`, {
          duration: 4000,
        });
        break;
      default:
        console.log("Unknown realtime event:", event);
    }
  }, []);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (!token || socketRef.current?.connected) return;

    const socket = io(WS_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socket.on('connect', () => {
      console.log('WebSocket connected');
      setConnected(true);
      setConnectionType('websocket');
      
      // Stop polling if it was running
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    });

    socket.on('disconnect', () => {
      console.log('WebSocket disconnected');
      setConnected(false);
      setConnectionType(null);
    });

    socket.on('connect_error', (error) => {
      console.log('WebSocket connection error:', error.message);
      // Fall back to polling
      if (!pollTimeoutRef.current) {
        startPolling();
      }
    });

    socket.on('realtime_event', (event) => {
      handleEvent(event);
    });

    socketRef.current = socket;
  }, [token, handleEvent]);

  // Polling fallback
  const pollEvents = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (lastTimestampRef.current) {
        params.append("since", lastTimestampRef.current);
      }
      
      const res = await axios.get(`${API}/realtime/poll?${params}`);
      
      if (res.data.events && res.data.events.length > 0) {
        res.data.events.forEach((event) => {
          handleEvent(event);
        });
      }
      
      lastTimestampRef.current = res.data.timestamp;
      setConnected(true);
      setConnectionType('polling');
    } catch (error) {
      console.error("Polling failed:", error);
      setConnected(false);
    }
  }, [handleEvent]);

  const startPolling = useCallback(() => {
    if (socketRef.current?.connected) return; // Don't poll if WebSocket is connected
    
    pollEvents();
    pollTimeoutRef.current = setTimeout(startPolling, POLL_INTERVAL);
  }, [pollEvents]);

  const publishEvent = useCallback(async (eventType, data, targetUsers) => {
    // Try WebSocket first
    if (socketRef.current?.connected) {
      socketRef.current.emit('publish_event', { eventType, data, targetUsers });
      return;
    }
    
    // Fall back to HTTP
    try {
      await axios.post(`${API}/realtime/publish`, null, {
        params: {
          event_type: eventType,
          target_users: targetUsers.join(","),
        },
        data: { data },
      });
    } catch (error) {
      console.error("Failed to publish event:", error);
    }
  }, []);

  const clearEvents = useCallback(() => {
    setEvents([]);
  }, []);

  // Main effect
  useEffect(() => {
    if (!enabled) {
      // Cleanup
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
      setConnected(false);
      setConnectionType(null);
      return;
    }

    // Try WebSocket first
    connectWebSocket();

    // Start polling as fallback after a delay if WebSocket doesn't connect
    const pollFallbackTimeout = setTimeout(() => {
      if (!socketRef.current?.connected && !pollTimeoutRef.current) {
        console.log('WebSocket not connected, falling back to polling');
        startPolling();
      }
    }, 3000);

    return () => {
      clearTimeout(pollFallbackTimeout);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, [enabled, connectWebSocket, startPolling]);

  return {
    events,
    connected,
    connectionType,
    publishEvent,
    clearEvents,
    socket: socketRef.current,
  };
};

export default useRealtime;
